import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../../api';

import {
  createStudioAnalyticsId,
} from '../../../studio/avatar/services/studioAnalyticsApi';

import AvatarStudioAwarenessDrawer from './AvatarStudioAwarenessDrawer';

const CAMPAIGN_KEY = 'avatar_studio';
const BASE_PATH = '/studio/awareness';
const OPEN_DELAY_MS = 900;

// Retry once using the same ID.
// Backend duplicate protection prevents counting the action twice.
async function submitInteraction(path, impressionId) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await api.post(
        `${BASE_PATH}/${path}`,
        { impressionId },
        { timeout: 5000 }
      );

      return true;
    } catch (error) {
      const status = error?.response?.status;

      // Validation, authentication and conflict errors
      // will not be fixed by repeating the same request.
      if (status && status < 500 && status !== 429) {
        return false;
      }

      if (status === 429) {
        return false;
      }
    }
  }

  return false;
}

export default function AvatarStudioAwarenessCampaign({
  user,
  enabled = false,
  onEligibilityResolved,
  onOpened,
  onFinished,
}) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const callbacksRef = useRef({});
  const checkedRef = useRef(false);
  const appearanceRef = useRef(null);
  const generationRef = useRef(0);

  const username = String(user?.username || '')
    .trim()
    .toLowerCase();

  const accountId = String(user?._id || '');

  callbacksRef.current = {
    onEligibilityResolved,
    onOpened,
    onFinished,
  };

  // Account changes must not reuse another account's appearance.
  useEffect(() => {
    generationRef.current += 1;
    checkedRef.current = false;
    appearanceRef.current = null;
    setOpen(false);
  }, [username, accountId]);

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      checkedRef.current = false;
      appearanceRef.current = null;
      return undefined;
    }

    if (!username || !accountId || checkedRef.current) {
      return undefined;
    }

    checkedRef.current = true;

    let cancelled = false;
    const controller = new AbortController();
    const generation = generationRef.current;

    function isCurrent() {
      return (
        !cancelled &&
        generationRef.current === generation
      );
    }

    function resolveEligibility(eligible) {
      if (!isCurrent()) return;

      callbacksRef.current.onEligibilityResolved?.({
        campaignKey: CAMPAIGN_KEY,
        eligible,
      });
    }

    async function checkAndOpen() {
      try {
        const { data: eligibility } = await api.get(
          `${BASE_PATH}/eligibility`,
          {
            signal: controller.signal,
            timeout: 5000,
          }
        );

        if (!isCurrent()) return;

        if (eligibility?.eligible !== true) {
          resolveEligibility(false);
          return;
        }

        const impressionId = createStudioAnalyticsId();

        const { data: reservation } = await api.post(
          `${BASE_PATH}/reserve`,
          { reservationId: impressionId },
          {
            signal: controller.signal,
            timeout: 5000,
          }
        );

        if (!isCurrent()) return;

        if (
          reservation?.eligible !== true ||
          reservation.reservationId !== impressionId
        ) {
          resolveEligibility(false);
          return;
        }

        appearanceRef.current = {
          impressionId,
          generation,
          shownPromise: null,
          opened: false,
          finished: false,
        };

        resolveEligibility(true);
        setOpen(true);
      } catch {
        // An unavailable campaign must not interrupt Home
        // or block the next awareness campaign.
        resolveEligibility(false);
      }
    }

    const timer = window.setTimeout(
      checkAndOpen,
      OPEN_DELAY_MS
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [enabled, username, accountId]);

  function ensureShown(appearance) {
    if (!appearance.shownPromise) {
      appearance.shownPromise = submitInteraction(
        'shown',
        appearance.impressionId
      );
    }

    return appearance.shownPromise;
  }

  function handleShown() {
    const appearance = appearanceRef.current;

    if (
      !appearance ||
      appearance.finished ||
      appearance.generation !== generationRef.current
    ) {
      return;
    }

    if (!appearance.opened) {
      appearance.opened = true;

      callbacksRef.current.onOpened?.({
        campaignKey: CAMPAIGN_KEY,
      });
    }

    void ensureShown(appearance);
  }

  function finish(action) {
    const appearance = appearanceRef.current;

    if (
      !appearance ||
      appearance.finished ||
      appearance.generation !== generationRef.current
    ) {
      return;
    }

    // Guard against double taps and competing dismiss actions.
    appearance.finished = true;

    // A very fast tap may happen before onShown's paint callback.
    // Record visibility before submitting the terminal action.
    const shownPromise = ensureShown(appearance);

    setOpen(false);

    // Continue reporting after navigation without delaying it.
    void (async () => {
      const shownRecorded = await shownPromise;

      if (!shownRecorded) return;

      await submitInteraction(
        action === 'started' ? 'start' : 'dismiss',
        appearance.impressionId
      );
    })();

    callbacksRef.current.onFinished?.({
      campaignKey: CAMPAIGN_KEY,
      outcome:
        action === 'started' ? 'completed' : 'dismissed',
      action:
        action === 'started' ? 'start' : 'maybe_later',
    });

    if (action === 'started') {
      // The backend records the Start click.
      // Studio records its own open with this entry point.
      navigate('/studio/avatar', {
        state: {
          studioEntryPoint: 'home_awareness',
        },
      });
    }
  }

  return (
    <AvatarStudioAwarenessDrawer
      open={enabled && open}
      onShown={handleShown}
      onStart={() => finish('started')}
      onDismiss={() => finish('dismissed')}
    />
  );
}
